// The single localStorage entry every setup for this browser lives in — one
// key, instead of the old one-key-per-field-per-setup layout (setups-index,
// active-setup-id, and a separate setup:<id>:data/setup:<id>:undo-history
// pair for every setup — see migrate() below). state.js reads/writes the
// currently-active setup's record here on every edit; setups.js reads/
// writes it for create/rename/delete/list. Both are just mutations on the
// same reactive object, so any page that imports this module sees live,
// consistent data — unlike the old design, where a setup's data only
// became reactive/correct once state.js's loadSetup() had actually run at
// least once *on that specific page* (the separate /setups/ management
// page's own bundle never called it, which is what made rename/delete
// there unreliable).
import { usePersisted, readJSON, writeJSON } from './use_persisted.js';
import { CALCULATOR_VERSION } from './calc.js';
import defaultSetup from './default_setup.json';

const STORE_KEY = 'setups-store';

export function genId() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// A calc is only trustworthy against the exact CALCULATOR_VERSION it was
// computed with — a value computed by an older client (whether from this
// browser's own past, or pulled from another device via sync) must not be
// trusted just because it's present. Centralizing this one check replaces
// three separate, slightly different version checks the old code had
// scattered across loadDataFor/pullAll/loadFreshAfterLogin — the latter two
// didn't actually apply it to synced-in data at all, so bumping
// CALCULATOR_VERSION didn't reliably invalidate a result pulled from another
// device.
export function isCalcFresh(calc) {
  return calc != null && calc.calculator_version === CALCULATOR_VERSION;
}

// A brand new setup — default_setup.json's sight_settings/marks/calc are
// the entire default setup a fresh install starts with (sight_settings,
// marks, AND the precomputed fit for them, all generated together by
// scripts/generate_default_setup.mjs — see its header comment), so a new
// setup shows a real, already-computed result instead of running
// findVelocityAndDrag/calcOffset in the visitor's own browser. Timestamps
// are stamped to the actual moment this setup is created (default_setup.json
// carries updated_at: null on each field, since it's generated, not
// "just edited").
export function createDefaultRecord(name) {
  const now = Date.now();
  return {
    name,
    sight_settings: { ...defaultSetup.sight_settings, updated_at: now },
    marks: defaultSetup.marks.map((m) => ({ ...m, updated_at: now })),
    description: '',
    calc: { ...defaultSetup.calc, calculator_version: CALCULATOR_VERSION },
    updated_at: now,
    undo: { stack: [], redo: [] },
  };
}

// ── One-time migration from the old per-key-per-setup layout ──
//
// Old layout: 'setups-index' (an id array) + 'active-setup-id', and per id
// 'setup:<id>:data' / 'setup:<id>:undo-history' (see the old setup_keys.js's
// scopedKey — still used elsewhere for unrelated per-setup UI scratch state,
// see mark.vue/full_marks.vue/trajectory.vue, but no longer for this core
// data). Older still: a single flat 'sight_settings_and_marks' key from
// before multi-setup support existed at all — folded straight into the new
// shape here rather than through that old migration's own intermediate
// per-key form.
function migrate() {
  if (readJSON(STORE_KEY, null) !== null) return;

  const oldIndex = readJSON('setups-index', null);
  if (oldIndex === null) {
    const legacy = readJSON('sight_settings_and_marks', null);
    if (legacy !== null) {
      const id = genId();
      writeJSON(STORE_KEY, {
        activeId: id,
        setups: {
          [id]: {
            name: 'Default',
            sight_settings: legacy.sight_settings,
            marks: legacy.marks,
            calc: null,
            updated_at: Date.now(),
            undo: { stack: [], redo: [] },
          },
        },
      });
      localStorage.removeItem('sight_settings_and_marks');
      return;
    }

    // Truly fresh install — nothing to migrate, bootstrap one default setup.
    const id = genId();
    writeJSON(STORE_KEY, { activeId: id, setups: { [id]: createDefaultRecord('Default') } });
    return;
  }

  // Defensive: a bug in an earlier build could in principle have pushed a
  // non-string entry into 'setups-index' (an object id would otherwise get
  // silently coerced to the literal key "[object Object]" below) — skip
  // anything that isn't a real id rather than migrating garbage forward.
  const validIds = oldIndex.filter((id) => typeof id === 'string' && id.length > 0);

  const setups = {};
  for (const id of validIds) {
    const data = readJSON(`setup:${id}:data`, null);
    const undo = readJSON(`setup:${id}:undo-history`, null);
    setups[id] = data
      ? {
          name: data.name ?? 'Setup',
          sight_settings: data.sight_settings,
          marks: data.marks,
          calc: (data.calculator_version ?? 0) === CALCULATOR_VERSION && data.calc != null
            ? { ...data.calc, calculator_version: CALCULATOR_VERSION }
            : null,
          updated_at: data.updated_at ?? 0,
          undo: undo ?? { stack: [], redo: [] },
        }
      : createDefaultRecord('Setup');
  }

  // Clean up every old per-id key regardless of validity — a garbage
  // entry's own setup:<id>:* keys (if it somehow had any) are just as
  // stale as a valid one's.
  for (const id of oldIndex) {
    localStorage.removeItem(`setup:${id}:data`);
    localStorage.removeItem(`setup:${id}:undo-history`);
  }

  let activeId = readJSON('active-setup-id', null);
  if (typeof activeId !== 'string' || !setups[activeId]) activeId = validIds[0];
  if (!activeId) {
    // Every entry in oldIndex was garbage — fall back to one fresh setup
    // rather than ending up with an empty store.
    activeId = genId();
    setups[activeId] = createDefaultRecord('Default');
  }

  writeJSON(STORE_KEY, { activeId, setups });
  localStorage.removeItem('setups-index');
  localStorage.removeItem('active-setup-id');
}

migrate();

// The one reactive, persisted store. Exported directly (rather than behind
// getter/setter wrapper functions) — state.js and setups.js both just read
// and mutate `store.value.setups`/`store.value.activeId` in place, same as
// they'd mutate any other reactive object; wrapping every access would only
// add indirection without removing any actual complexity.
export const store = usePersisted(STORE_KEY, { activeId: null, setups: {} });
